/**
 * What the app knows about symbols: the search index, and the names on screen.
 *
 * Both are **facts about the page, not about a component**, and that is the
 * whole reason this module exists. They used to be `useState` inside
 * `<SymbolSearch>`, which is mounted once per chart toolbar — so "is the index
 * loaded" had one answer per mounted box, and they could disagree. App-wide
 * typing goes to one of those boxes, so the disagreement was reachable: the
 * letter landed on a box whose copy was cold and was dropped.
 *
 * Module scope with subscribers rather than a store or a context. Each value is
 * written at most once per page load, so `useSyncExternalStore` would be
 * ceremony around a boolean, and a provider would put a re-render boundary
 * around the whole shell to publish something nothing re-reads.
 *
 * Nothing here imports React beyond the two hooks at the bottom, and nothing
 * here renders — which is what makes it testable, unlike everything that
 * reaches a chart.
 */

import { useEffect, useReducer } from "react";

import { fetchAllSymbols, fetchSymbolNames, symbolsConfigured, type SymbolRow } from "../api/symbols";
import { canonicalSymbol } from "./composite";

export type { SymbolRow };

/**
 * The ~14,000-row search index, fetched at most once per page load.
 *
 * **Loaded on demand, never on mount.** It is ~2.7MB over the wire and most
 * sessions never search — a chart arrives with a symbol already on it. Paying
 * that on app load taxes every user for a feature some of them use once.
 *
 * A *failure* is deliberately not cached: `undefined` usually means qv-data is
 * not up yet, and a developer who starts it after the app should get a working
 * search on the next try rather than after a reload.
 */
let indexPromise: Promise<readonly SymbolRow[] | undefined> | null = null;
let indexRows: readonly SymbolRow[] | undefined;
const indexListeners = new Set<() => void>();

export function loadSymbolIndex(): Promise<readonly SymbolRow[] | undefined> {
  // Checked here rather than left to `fetchAllSymbols` so an unconfigured app
  // never even queues a request it knows the answer to.
  if (!symbolsConfigured) return Promise.resolve(undefined);
  if (!indexPromise) {
    indexPromise = fetchAllSymbols().then((rows) => {
      if (!rows) {
        indexPromise = null;
        return undefined;
      }
      indexRows = rows;
      for (const notify of indexListeners) notify();
      return rows;
    });
  }
  return indexPromise;
}

/**
 * "Do we know this symbol?", answering for either spelling.
 *
 * The resolver `isComposite` needs for a string whose only operator is `-`, and
 * the reason it is here rather than inlined at each call site: **the index is
 * dot-spelled and a saved workspace may not be.** `ChartGroup.symbol` has held
 * `BRK-B` since before the universe was respelled, and a resolver that only
 * looked for the exact string would answer "unknown" — after which `BRK-B`
 * reads as a spread of `BRK` and `B`, and a working chart acquires a company
 * profile it cannot have and loses its Plans button.
 *
 * `undefined` when the index has not landed, which `isComposite` treats as "no
 * resolver" and therefore as "a hyphen is part of the ticker" — the safe half.
 */
export function symbolResolver(
  rows: readonly SymbolRow[] | undefined,
): ((symbol: string) => boolean) | undefined {
  if (!rows) return undefined;
  const known = new Set(rows.map((row) => row.symbol));
  return (symbol: string) => known.has(symbol) || known.has(canonicalSymbol(symbol));
}

/** The index if it has landed, without asking for it. */
export function symbolIndex(): readonly SymbolRow[] | undefined {
  return indexRows;
}

/**
 * Names for the tickers on screen, without the index.
 *
 * The toolbar box reads `AAPL - Apple Inc.`, and taking that from the index
 * meant the name only appeared once something had triggered the 2.7MB fetch —
 * so a freshly loaded app showed a bare ticker until you clicked. Searching is
 * a feature some sessions use once; the label is one every session reads, so it
 * gets its own small lookup.
 *
 * A ticker is **claimed before it is fetched** (`null` in the map), so a
 * re-render mid-flight cannot queue it twice. A failed lookup un-claims it,
 * because `undefined` means "we never found out" and a later mount should be
 * allowed to ask again — the same call `loadSymbolIndex` makes about a failed
 * index.
 */
const nameCache = new Map<string, string | null>();
const nameListeners = new Set<() => void>();
let nameBatch: Set<string> | null = null;

export function resolveName(symbol: string): void {
  if (!symbolsConfigured || !symbol || nameCache.has(symbol)) return;
  // Claimed. `null` reads as "no name" everywhere else, which is exactly what
  // should be rendered while the answer is on the wire.
  nameCache.set(symbol, null);
  if (nameBatch) {
    nameBatch.add(symbol);
    return;
  }
  // A microtask, because every toolbar mounts in the same tick: three charts
  // asking for three names must be one request, and the batch closes before the
  // first `await` anywhere.
  nameBatch = new Set([symbol]);
  queueMicrotask(() => {
    const asked = nameBatch!;
    nameBatch = null;
    void fetchSymbolNames([...asked]).then((names) => {
      if (!names) {
        // Un-claimed, so a later mount may try again. Nothing to notify: the
        // label is already the bare ticker, which is the answer on failure.
        for (const ticker of asked) nameCache.delete(ticker);
        return;
      }
      for (const ticker of asked) nameCache.set(ticker, names[ticker] ?? null);
      for (const notify of nameListeners) notify();
    });
  });
}

/** The name we know for a ticker, from either source. The index wins. */
export function nameFor(symbol: string): string | undefined {
  const fromIndex = indexRows?.find((row) => row.symbol === symbol)?.name;
  return fromIndex ?? nameCache.get(symbol) ?? undefined;
}

/** `AAPL` plus a name we know becomes `AAPL - Apple Inc.` — for display only. */
export function symbolLabel(symbol: string, name: string | null | undefined): string {
  return name ? `${symbol} - ${name}` : symbol;
}

/** Re-render this component when one of the module-scope facts changes. */
function useBump(listeners: Set<() => void>): void {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    listeners.add(bump);
    return () => {
      listeners.delete(bump);
    };
  }, [listeners]);
}

/**
 * The index, subscribing to its arrival.
 *
 * `load` asks for it — callers that need it (the dialog) call this on mount;
 * callers that only want it if it happens to be there (the box) do not.
 */
export function useSymbolIndex(load: boolean): readonly SymbolRow[] | undefined {
  useBump(indexListeners);
  useEffect(() => {
    if (load) void loadSymbolIndex();
  }, [load]);
  return indexRows;
}

/**
 * One ticker's company name, asking for it if we do not have it.
 *
 * Deliberately not memoised on the index: `nameFor` scans it, but only for a
 * ticker the small lookup missed, and only for the handful of boxes on screen.
 */
export function useSymbolName(symbol: string): string | undefined {
  useBump(nameListeners);
  useBump(indexListeners);
  useEffect(() => {
    resolveName(symbol);
  }, [symbol]);
  return nameFor(symbol);
}
